// Content manifests: the only interface between tools/ and the app (v2 spec §3).
// Built by `npm run content:build` into public/content/<themeId>/manifest.json
// (one per theme/city) plus the index public/content/themes.json. File paths
// inside a manifest are relative to that manifest's own URL; paths in
// themes.json are relative to themes.json.

export type Layer = { file: string; index: number };

export type Circle = { x: number; y: number; r: number; layer: number };

/** A rune (符文): name shown in the UI, icon = key of RUNE_ICONS (src/ui/runeIcons.ts), plus its circle on the card. */
export type Rune = Circle & { name: string; icon: string };

export type Place = {
  id: string; // 'bakery'
  title: string; // '面包坊'
  story: string; // shown after completing the puzzle
  teaser: string; // map hint while unlocked but not completed
  unlockOrder: number; // 0,1,2 unlocked at start; the rest in order
  image: string; // original image (webp)
  thumb: string; // small preview (webp, long side 320) for the album, city select and daily page
  layers: Layer[]; // layer 0 = opaque back plate (nearer content inpainted out); 1.. = one depth band each
  // Circle convention (also for non-square pictures): x = px / width, y = py / height, r = radius px / width.
  cat: Circle; // normalised image coords; layer computed by tools/layers.py
  /** optional second find in the pop-up (tarot 符文): the card's own symbol; same circle convention as `cat` */
  rune?: Rune;
  hotspot: Circle; // normalised map coords (same convention on the map); layer used after the ending makes the map 3D
  cutThresholds?: number[]; // optional manual override of the layer cuts (3..5)
};

/** A run of places (by unlockOrder, inclusive) shown under one title on the map / album. */
export type Chapter = { title: string; from: number; to: number };

export type Theme = {
  id: string; // 'paris' — must equal the directory name
  title: string; // '巴黎街角'
  subtitle: string;
  endingStory: string; // shown after the last place of this city
  order: number; // 0,1,2 — journey order (cities); tarot = 3
  /** number of places; default PLACES_PER_THEME (8). Tarot: 22 */
  placeCount?: number;
  /** optional chapters covering unlockOrder 0..placeCount-1 in order (tarot: 0–7, 8–14, 15–21) */
  chapters?: Chapter[];
  /** open from the start, outside the city chain (cities open one after another; tarot is always open) */
  alwaysOpen?: boolean;
};

export type Manifest = {
  version: 2;
  theme: Theme;
  /** style 'night' = dark map (tarot star chart): locked/unlocked covers are a dark night veil instead of beige paper */
  map: { image: string; layers: Layer[]; size: [number, number]; cutThresholds?: number[]; style?: 'paper' | 'night' };
  /** [width, height] of every place picture; written by tools/layers.py only when it is not square (tarot 1024×1536) */
  imageSize?: [number, number];
  places: Place[];
};

/** place count a theme declares (default 8) */
export const placeCountOf = (t: Theme): number => t.placeCount ?? PLACES_PER_THEME;
/** place picture width / height (1 = square) */
export const imageAspect = (m: Manifest): number => (m.imageSize ? m.imageSize[0] / m.imageSize[1] : 1);
/** chapter of an unlockOrder, or null when the theme has no chapters */
export const chapterOf = (t: Theme, unlockOrder: number): Chapter | null =>
  t.chapters?.find((c) => unlockOrder >= c.from && unlockOrder <= c.to) ?? null;

export type ThemesIndexEntry = { id: string; title: string; subtitle: string; order: number; manifest: string; cover: string };
export type ThemesIndex = { version: 2; themes: ThemesIndexEntry[] };

export const PLACES_PER_THEME = 8;
export const MAX_PLACES_PER_THEME = 30;
export const MIN_LAYERS = 4;
export const MAX_LAYERS = 6;

export class ManifestError extends Error {
  constructor(public readonly issues: string[]) {
    super(`invalid content manifest:\n  - ${issues.join('\n  - ')}`);
    this.name = 'ManifestError';
  }
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown): v is number => Number.isInteger(v);
const isStr = (v: unknown): v is string => typeof v === 'string';
const SLUG = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Structural + semantic checks on an untrusted manifest. Returns every issue
 * found (empty array = valid). Does not touch the file system / network.
 */
export function manifestIssues(raw: unknown, opts: { themeId?: string; runeIcons?: readonly string[] } = {}): string[] {
  const issues: string[] = [];
  const bad = (path: string, msg: string) => issues.push(`${path}: ${msg}`);

  const str = (o: Obj, k: string, path: string, nonEmpty = true) => {
    if (!isStr(o[k])) bad(`${path}.${k}`, 'must be a string');
    else if (nonEmpty && o[k] === '') bad(`${path}.${k}`, 'must not be empty');
  };
  const noExtra = (o: Obj, allowed: string[], path: string) => {
    for (const k of Object.keys(o)) if (!allowed.includes(k)) bad(`${path}.${k}`, 'unknown field');
  };

  const layers = (v: unknown, path: string): number => {
    if (!Array.isArray(v)) {
      bad(path, 'must be an array');
      return 0;
    }
    if (v.length < MIN_LAYERS || v.length > MAX_LAYERS)
      bad(path, `must have ${MIN_LAYERS}..${MAX_LAYERS} layers, has ${v.length}`);
    v.forEach((l, i) => {
      const p = `${path}[${i}]`;
      if (!isObj(l)) return bad(p, 'must be an object');
      noExtra(l, ['file', 'index'], p);
      str(l, 'file', p);
      if (l.index !== i) bad(`${p}.index`, `must be ${i}`);
    });
    return v.length;
  };

  const cuts = (o: Obj, path: string, layerCount: number) => {
    if (o.cutThresholds === undefined) return;
    const c = o.cutThresholds;
    const p = `${path}.cutThresholds`;
    if (!Array.isArray(c) || !c.every(isNum)) return bad(p, 'must be an array of numbers');
    if (c.length < 3 || c.length > 5) bad(p, `must have 3..5 values, has ${c.length}`);
    if (c.some((x) => x <= 0 || x >= 1)) bad(p, 'values must be in (0,1)');
    if (c.some((x, i) => i > 0 && x <= c[i - 1])) bad(p, 'values must be strictly increasing');
    if (layerCount && layerCount !== c.length + 1)
      bad(p, `${c.length} thresholds should give ${c.length + 1} layers, found ${layerCount} (rebuild)`);
  };

  const circle = (v: unknown, path: string, layerCount: number, extra: string[] = []) => {
    if (!isObj(v)) return bad(path, 'missing annotation');
    noExtra(v, ['x', 'y', 'r', 'layer', ...extra], path);
    for (const k of ['x', 'y'] as const)
      if (!isNum(v[k]) || v[k] < 0 || v[k] > 1) bad(`${path}.${k}`, 'must be a number in 0..1');
    if (!isNum(v.r) || v.r <= 0 || v.r > 0.5) bad(`${path}.r`, 'must be a number in (0, 0.5]');
    if (!isInt(v.layer) || v.layer < 0 || (layerCount > 0 && v.layer >= layerCount))
      bad(`${path}.layer`, `must be an integer layer index in 0..${Math.max(layerCount - 1, 0)}`);
  };

  if (!isObj(raw)) return ['manifest: must be a JSON object'];
  noExtra(raw, ['version', 'theme', 'map', 'imageSize', 'places'], 'manifest');
  if (raw.version !== 2) bad('version', 'must be 2');
  if (raw.imageSize !== undefined) {
    const s = raw.imageSize;
    if (!Array.isArray(s) || s.length !== 2 || !s.every((n) => isInt(n) && n > 0)) bad('imageSize', 'must be [width, height] positive integers');
  }
  let placeCount = PLACES_PER_THEME;

  const theme = raw.theme;
  if (!isObj(theme)) bad('theme', 'must be an object');
  else {
    noExtra(theme, ['id', 'title', 'subtitle', 'endingStory', 'order', 'placeCount', 'chapters', 'alwaysOpen'], 'theme');
    if (!isStr(theme.id) || !SLUG.test(theme.id)) bad('theme.id', "must be a lowercase slug (a-z, 0-9, '-')");
    else if (opts.themeId !== undefined && theme.id !== opts.themeId) bad('theme.id', `must be '${opts.themeId}'`);
    str(theme, 'title', 'theme');
    str(theme, 'subtitle', 'theme');
    str(theme, 'endingStory', 'theme');
    if (!isInt(theme.order) || theme.order < 0) bad('theme.order', 'must be an integer >= 0');
    if (theme.placeCount !== undefined) {
      if (!isInt(theme.placeCount) || theme.placeCount < 4 || theme.placeCount > MAX_PLACES_PER_THEME)
        bad('theme.placeCount', `must be an integer in 4..${MAX_PLACES_PER_THEME}`);
      else placeCount = theme.placeCount;
    }
    if (theme.alwaysOpen !== undefined && typeof theme.alwaysOpen !== 'boolean') bad('theme.alwaysOpen', 'must be a boolean');
    if (theme.chapters !== undefined) {
      const ch = theme.chapters;
      if (!Array.isArray(ch) || ch.length === 0) bad('theme.chapters', 'must be a non-empty array');
      else {
        let next = 0;
        ch.forEach((c, i) => {
          const p = `theme.chapters[${i}]`;
          if (!isObj(c)) return bad(p, 'must be an object');
          noExtra(c, ['title', 'from', 'to'], p);
          str(c, 'title', p);
          if (!isInt(c.from) || !isInt(c.to) || c.from > c.to) return bad(p, 'from/to must be integers with from <= to');
          if (c.from !== next) bad(`${p}.from`, `must be ${next} (chapters cover every unlockOrder in order)`);
          next = c.to + 1;
        });
        if (next !== placeCount) bad('theme.chapters', `must end at unlockOrder ${placeCount - 1}, ends at ${next - 1}`);
      }
    }
  }

  let mapLayers = 0;
  const map = raw.map;
  if (!isObj(map)) bad('map', 'must be an object');
  else {
    noExtra(map, ['image', 'layers', 'size', 'cutThresholds', 'style'], 'map');
    if (map.style !== undefined && map.style !== 'paper' && map.style !== 'night') bad('map.style', "must be 'paper' or 'night'");
    str(map, 'image', 'map');
    mapLayers = layers(map.layers, 'map.layers');
    const s = map.size;
    if (!Array.isArray(s) || s.length !== 2 || !s.every((n) => isInt(n) && n > 0))
      bad('map.size', 'must be [width, height] positive integers');
    cuts(map, 'map', mapLayers);
  }

  const places = raw.places;
  if (!Array.isArray(places)) bad('places', 'must be an array');
  else {
    if (places.length !== placeCount) bad('places', `must have exactly ${placeCount} places (theme.placeCount), has ${places.length}`);
    const ids = new Set<string>();
    const orders: number[] = [];
    places.forEach((pl, i) => {
      const path = `places[${i}]`;
      if (!isObj(pl)) return bad(path, 'must be an object');
      const where = isStr(pl.id) ? `places[${pl.id}]` : path;
      noExtra(
        pl,
        ['id', 'title', 'story', 'teaser', 'unlockOrder', 'image', 'thumb', 'layers', 'cat', 'rune', 'hotspot', 'cutThresholds'],
        where,
      );
      if (!isStr(pl.id) || !SLUG.test(pl.id) || pl.id === 'map')
        bad(`${where}.id`, "must be a lowercase slug (a-z, 0-9, '-') and not 'map'");
      else if (ids.has(pl.id)) bad(`${where}.id`, 'duplicate id');
      else ids.add(pl.id);
      for (const k of ['title', 'story', 'teaser', 'image', 'thumb']) str(pl, k, where);
      if (!isInt(pl.unlockOrder) || pl.unlockOrder < 0) bad(`${where}.unlockOrder`, 'must be an integer >= 0');
      else orders.push(pl.unlockOrder);
      const n = layers(pl.layers, `${where}.layers`);
      if (isStr(pl.image) && Array.isArray(pl.layers) && pl.layers.some((l) => isObj(l) && l.index !== 0 && l.file === pl.image))
        bad(`${where}.layers`, 'a foreground layer must not be the untouched `image`');
      circle(pl.cat, `${where}.cat`, n);
      if (pl.rune !== undefined) {
        circle(pl.rune, `${where}.rune`, n, ['name', 'icon']);
        if (isObj(pl.rune)) {
          str(pl.rune, 'name', `${where}.rune`);
          str(pl.rune, 'icon', `${where}.rune`);
          if (isStr(pl.rune.icon) && opts.runeIcons && !opts.runeIcons.includes(pl.rune.icon))
            bad(`${where}.rune.icon`, `unknown icon '${pl.rune.icon}' (see src/ui/runeIcons.ts)`);
        }
      }
      circle(pl.hotspot, `${where}.hotspot`, mapLayers);
      cuts(pl, where, n);
    });
    const sorted = [...orders].sort((a, b) => a - b);
    if (new Set(sorted).size !== sorted.length) bad('places[].unlockOrder', `must be unique, got ${sorted.join(',')}`);
    else if (sorted.some((o, i) => o !== i))
      bad('places[].unlockOrder', `must be contiguous from 0, got ${sorted.join(',')}`);
  }
  return issues;
}

/** Structural checks on themes.json (cross-checks against the manifests are in tools/validate.ts). */
export function themesIndexIssues(raw: unknown): string[] {
  const issues: string[] = [];
  const bad = (path: string, msg: string) => issues.push(`${path}: ${msg}`);
  if (!isObj(raw)) return ['themes.json: must be a JSON object'];
  for (const k of Object.keys(raw)) if (!['version', 'themes'].includes(k)) bad(`themes.json.${k}`, 'unknown field');
  if (raw.version !== 2) bad('themes.json.version', 'must be 2');
  if (!Array.isArray(raw.themes) || raw.themes.length === 0) {
    bad('themes.json.themes', 'must be a non-empty array');
    return issues;
  }
  const ids = new Set<string>();
  const orders = new Set<number>();
  let prev = -Infinity;
  raw.themes.forEach((t, i) => {
    const p = `themes.json.themes[${i}]`;
    if (!isObj(t)) return bad(p, 'must be an object');
    for (const k of Object.keys(t))
      if (!['id', 'title', 'subtitle', 'order', 'manifest', 'cover'].includes(k)) bad(`${p}.${k}`, 'unknown field');
    for (const k of ['id', 'title', 'subtitle', 'manifest', 'cover'])
      if (!isStr(t[k]) || t[k] === '') bad(`${p}.${k}`, 'must be a non-empty string');
    if (isStr(t.id)) {
      if (!SLUG.test(t.id)) bad(`${p}.id`, 'must be a lowercase slug');
      if (ids.has(t.id)) bad(`${p}.id`, 'duplicate id');
      ids.add(t.id);
      if (t.manifest !== `${t.id}/manifest.json`) bad(`${p}.manifest`, `must be '${t.id}/manifest.json'`);
    }
    if (!isInt(t.order) || t.order < 0) bad(`${p}.order`, 'must be an integer >= 0');
    else {
      if (orders.has(t.order)) bad(`${p}.order`, 'duplicate order');
      if (t.order < prev) bad(`${p}.order`, 'themes must be sorted by order');
      orders.add(t.order);
      prev = t.order;
    }
  });
  return issues;
}

/** Throws ManifestError listing every problem; otherwise returns the typed manifest. */
export function parseManifest(raw: unknown, opts: { themeId?: string } = {}): Manifest {
  const issues = manifestIssues(raw, opts);
  if (issues.length) throw new ManifestError(issues);
  return raw as Manifest;
}

/** Every full-size file a place depends on (deduplicated), relative to the manifest; `thumb` is separate. */
export function placeFiles(place: Place): string[] {
  return [...new Set([place.image, ...place.layers.map((l) => l.file)])];
}

/**
 * Fetch + validate the manifest. File paths in the result are resolved to
 * absolute URLs against the manifest URL, so callers can load them directly.
 */
export async function loadManifest(url: string): Promise<Manifest> {
  const base = typeof document !== 'undefined' ? document.baseURI : 'http://localhost/';
  const manifestUrl = new URL(url, base);
  const res = await fetch(manifestUrl);
  if (!res.ok) throw new Error(`content manifest: HTTP ${res.status} for ${manifestUrl}`);
  const m = parseManifest(await res.json());
  const abs = (f: string) => new URL(f, manifestUrl).href;
  const fixLayers = (ls: Layer[]) => ls.map((l) => ({ ...l, file: abs(l.file) }));
  return {
    ...m,
    map: { ...m.map, image: abs(m.map.image), layers: fixLayers(m.map.layers) },
    places: m.places.map((p) => ({ ...p, image: abs(p.image), thumb: abs(p.thumb), layers: fixLayers(p.layers) })),
  };
}

/** One row of public/content/themes.json; `manifest` and `cover` are absolute URLs after loadThemes. */
export type ThemeEntry = ThemesIndexEntry;

/** Fetch + validate public/content/themes.json, sorted by order, URLs made absolute. */
export async function loadThemes(url?: string): Promise<ThemeEntry[]> {
  const base = typeof document !== 'undefined' ? document.baseURI : 'http://localhost/';
  const indexUrl = new URL(url ?? `${import.meta.env?.BASE_URL ?? '/'}content/themes.json`, base);
  const res = await fetch(indexUrl);
  if (!res.ok) throw new Error(`content themes: HTTP ${res.status} for ${indexUrl}`);
  const raw = await res.json();
  const issues = themesIndexIssues(raw);
  if (issues.length) throw new ManifestError(issues);
  const abs = (f: string) => new URL(f, indexUrl).href;
  return (raw as ThemesIndex).themes
    .map((t) => ({ id: t.id, title: t.title, subtitle: t.subtitle, order: t.order, manifest: abs(t.manifest), cover: abs(t.cover) }))
    .sort((a, b) => a.order - b.order);
}
